import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './FeedbackStatusNotification.css';

// Hilfsfunktion: Formatiert relatives Datum (t für Übersetzungen)
const formatRelativeDate = (t, timestamp) => {
  if (!timestamp) return t('feedback_status.unknown');
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

  if (diffMins < 1) return t('feedback_status.just_now');
  if (diffMins < 60) return t('feedback_status.minutes_ago', { count: diffMins });
  if (diffHours < 24) {
    if (now.toDateString() === date.toDateString()) return t('feedback_status.today_at', { time: timeStr });
    return t('feedback_status.hours_ago', { count: diffHours });
  }
  if (diffDays === 1) {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (yesterday.toDateString() === date.toDateString()) return t('feedback_status.yesterday_at', { time: timeStr });
    return t('feedback_status.yesterday');
  }
  if (diffDays < 7) return t('feedback_status.days_ago', { count: diffDays });
  return date.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Hilfsfunktion: Prüft ob Update neu ist (< 24 Stunden)
// const isNewUpdate = (statusChangedAt) => {
//   if (!statusChangedAt) return false;
//   const now = new Date();
//   const changedAt = new Date(statusChangedAt);
//   const diffMs = now - changedAt;
//   const diffHours = diffMs / 3600000;
//   return diffHours < 24;
// };

// Hilfsfunktion: Bestimmt den Typ des neuen Updates ('very_new' < 1 Stunde, 'new' < 24 Stunden, null)
const getNewUpdateType = (statusChangedAt) => {
  if (!statusChangedAt) return null;
  const now = new Date();
  const changedAt = new Date(statusChangedAt);
  const diffMs = now - changedAt;
  const diffHours = diffMs / 3600000;
  if (diffHours < 1) return 'very_new';
  if (diffHours < 24) return 'new';
  return null;
};

const FeedbackStatusNotification = ({
  backendUrl,
  userEmail,
  onClose,
  forceVisible = false, // Wenn true, wird die Komponente immer angezeigt (manuelles Öffnen)
  pendingFeedbackCount = 0, // Anzahl noch nicht hochgeladener Feedbacks (offline gespeichert)
  onOpenLoadPendingCount, // Wird beim Öffnen aufgerufen, damit die App die ausstehende Anzahl lädt
}) => {
  const { t } = useTranslation();
  const [feedbacks, setFeedbacks] = useState([]);
  const [isVisible, setIsVisible] = useState(false);

  // Beim Öffnen ausstehende Upload-Anzahl laden (für Anzeige „Noch X Feedbacks warten auf Upload“)
  useEffect(() => {
    if (typeof onOpenLoadPendingCount === 'function') onOpenLoadPendingCount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!backendUrl || !userEmail) return;

    const checkStatusUpdates = async () => {
      try {
        // Hole letzten Check-Zeitpunkt
        const lastCheck = localStorage.getItem('lastFeedbackCheck');
        const since = lastCheck || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(); // Default: letzte 7 Tage

        const normalizedUrl = backendUrl.replace(/\/$/, '');
        // Beim manuellen Öffnen (forceVisible) immer letzte 7 Tage, damit die Liste nicht beim 2. Aufruf leer ist
        // Nur beim automatischen Check: since = lastCheck oder 7 Tage
        const sinceForQuery = forceVisible
          ? new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
          : since;
        const apiUrl = `${normalizedUrl}/api/feedback/status-updates?user_email=${encodeURIComponent(userEmail)}&since=${encodeURIComponent(sinceForQuery)}`;

        const response = await fetch(apiUrl);
        if (response.ok) {
          const data = await response.json();
          const updates = data.feedbacks || [];
          // Pro Titel nur den neuesten Eintrag anzeigen (status_changed_at neueste)
          const byTitle = new Map();
          for (const f of updates) {
            const key = String(f.title || '').trim() || `id-${f.id}`;
            const existing = byTitle.get(key);
            if (!existing || new Date(f.status_changed_at || 0) > new Date(existing.status_changed_at || 0)) {
              byTitle.set(key, f);
            }
          }
          const uniqueByTitle = Array.from(byTitle.values()).sort((a, b) =>
            new Date(b.status_changed_at || 0) - new Date(a.status_changed_at || 0)
          );

          setFeedbacks(uniqueByTitle);
          // Wenn forceVisible (manuelles Öffnen) oder Updates vorhanden, sichtbar machen
          if (forceVisible || uniqueByTitle.length > 0) {
            setIsVisible(true);
          }
        } else {
          // Bei Fehler-Response, logge aber wirf keinen Fehler
          const errorText = await response.text().catch(() => t('feedback_status.unknown_error'));
          console.warn('[FeedbackStatusNotification] Backend-Fehler beim Laden der Status-Updates:', response.status, errorText);
        }
      } catch (error) {
        console.warn('[FeedbackStatusNotification] Fehler beim Laden der Status-Updates (wird ignoriert):', error.message);
      }
    };

    // Prüfe sofort beim Mount
    checkStatusUpdates();

    // Prüfe alle 30 Minuten (nur wenn nicht manuell geöffnet)
    if (!forceVisible) {
      const interval = setInterval(checkStatusUpdates, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [backendUrl, userEmail, forceVisible, t]);

  // Wenn nicht sichtbar UND nicht manuell geöffnet, nichts anzeigen
  // Wenn manuell geöffnet (forceVisible), immer anzeigen, auch wenn keine Updates
  if (!isVisible && !forceVisible) return null;

  const statusLabels = {
    NEW: t('feedback_status.status_NEW'),
    CONFIRMED: t('feedback_status.status_CONFIRMED'),
    IN_PROGRESS: t('feedback_status.status_IN_PROGRESS'),
    WAITING: t('feedback_status.status_WAITING'),
    RESOLVED: t('feedback_status.status_RESOLVED'),
    REJECTED: t('feedback_status.status_REJECTED'),
    DUPLICATE: t('feedback_status.status_DUPLICATE'),
  };

  const statusColors = {
    NEW: '#3b82f6',
    CONFIRMED: '#10b981',
    IN_PROGRESS: '#f59e0b',
    WAITING: '#6366f1',
    RESOLVED: '#10b981',
    REJECTED: '#ef4444',
    DUPLICATE: '#6b7280',
  };

  const priorityLabels = {
    LOW: t('feedback_status.priority_LOW'),
    MEDIUM: t('feedback_status.priority_MEDIUM'),
    HIGH: t('feedback_status.priority_HIGH'),
    CRITICAL: t('feedback_status.priority_CRITICAL'),
  };

  const priorityColors = {
    CRITICAL: '#dc2626',
    HIGH: '#ea580c',
    MEDIUM: '#f59e0b',
    LOW: '#10b981',
  };

  const handleClose = () => {
    if (feedbacks.length > 0) {
      const lastSeenTime = new Date().toISOString();
      localStorage.setItem('lastFeedbackCheck', lastSeenTime);
    }
    setIsVisible(false);
    if (onClose) onClose();
  };

  // Berechne Anzahl neuer Updates (seit lastCheck, wenn vorhanden)
  const lastCheck = localStorage.getItem('lastFeedbackCheck');
  const newUpdatesCount = lastCheck 
    ? feedbacks.filter(f => f.status_changed_at && new Date(f.status_changed_at) > new Date(lastCheck)).length
    : feedbacks.length; // Wenn kein lastCheck, sind alle "neu"
  
  // Formatiere Header-Text mit Unterscheidung zwischen neuen und allen Updates
  const getHeaderText = () => {
    if (feedbacks.length === 0) return `📬 ${t('feedback_status.header')}`;
    if (lastCheck && newUpdatesCount > 0 && newUpdatesCount < feedbacks.length) {
      return `📬 ${t('feedback_status.header_mixed', { newCount: newUpdatesCount, total: feedbacks.length })}`;
    } else if (lastCheck && newUpdatesCount > 0) {
      return `📬 ${t('feedback_status.header_new', { newCount: newUpdatesCount })}`;
    } else {
      return `📬 ${t('feedback_status.header_total', { total: feedbacks.length })}`;
    }
  };

  return (
    <div className="feedback-status-notification">
      <div className="feedback-status-header">
        <h3>{getHeaderText()}</h3>
        <button className="feedback-status-close" onClick={handleClose}>×</button>
      </div>
      {pendingFeedbackCount > 0 && (
        <div className="feedback-status-pending-uploads">
          <strong>{t('feedback_status.pending', { count: pendingFeedbackCount })}</strong>
          <br />
          <span style={{ fontSize: '0.9em' }}>{t('feedback_status.pending_hint')}</span>
        </div>
      )}
      <div className="feedback-status-list">
        {feedbacks.length === 0 ? (
          <div className="feedback-status-empty">
            {pendingFeedbackCount === 0 ? t('feedback_status.no_updates') : t('feedback_status.offline_only')}
          </div>
        ) : (
          feedbacks.map((feedback) => {
            const newUpdateType = getNewUpdateType(feedback.status_changed_at);
            const isNew = newUpdateType !== null;
            const isVeryNew = newUpdateType === 'very_new';
            const relativeDate = formatRelativeDate(t, feedback.status_changed_at);
            const absoluteDate = feedback.status_changed_at 
              ? new Date(feedback.status_changed_at).toLocaleString('de-DE', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              : t('feedback_status.unknown');
            
            return (
              <div 
                key={feedback.id} 
                className={`feedback-status-item ${isNew ? 'feedback-status-item-new' : ''} ${isVeryNew ? 'feedback-status-item-very-new' : ''}`}
              >
                <div className="feedback-status-item-header">
                  <span className="feedback-status-title">{feedback.title}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    {isVeryNew && (
                      <span className="feedback-status-very-new-badge">{t('feedback_status.badge_very_new')}</span>
                    )}
                    {isNew && !isVeryNew && (
                      <span className="feedback-status-new-badge">{t('feedback_status.badge_new')}</span>
                    )}
                    {feedback.priority && (
                      <span
                        className="feedback-status-priority-badge"
                        style={{ backgroundColor: priorityColors[feedback.priority] || '#6b7280' }}
                        title={t('feedback_status.priority_title', { priority: priorityLabels[feedback.priority] || feedback.priority })}
                      >
                        {priorityLabels[feedback.priority] || feedback.priority}
                      </span>
                    )}
                    <span
                      className="feedback-status-badge"
                      style={{ backgroundColor: statusColors[feedback.status] || '#6b7280' }}
                    >
                      {statusLabels[feedback.status] || feedback.status}
                    </span>
                  </div>
                </div>
                {feedback.admin_notes && (
                  <div className="feedback-status-notes">
                    <strong>{t('feedback_status.admin_note')}</strong> {feedback.admin_notes}
                  </div>
                )}
                <div className="feedback-status-meta">
                  <span className="feedback-status-relative-date" title={absoluteDate}>
                    {t('feedback_status.changed')} {relativeDate}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="feedback-status-footer">
        <button onClick={handleClose}>{t('feedback_status.close')}</button>
      </div>
    </div>
  );
};

export default FeedbackStatusNotification;
